import { useState, useEffect, useCallback, useRef } from 'react';
import { Asset } from '../../state/types';
import { AssetFilters, ImageFilter } from '../../components/assets/config';
import { useSession } from '../../context/SessionContext';
import { useAssetApi } from '../../api/useAssetApi';
import { useSearchParams } from 'react-router-dom';
import {
  buildAssetsSearchParams,
  cloneDefaultAssetFilters,
  hasAssetShareParams,
  readAssetsInitialState,
  saveAssetsNavState,
} from './assetsStatePersistence';

export const useAssets = () => {
  const initialStateRef = useRef(readAssetsInitialState(window.location.search));
  const { selectedSystem, isLoading: sessionIsLoading, setIsLoading } = useSession();
  const { getAssetsApi } = useAssetApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [page, setPage] = useState(initialStateRef.current.page);
  const [filters, setFilters] = useState<AssetFilters>(initialStateRef.current.filters);
  const [resultsPerPage, setResultsPerPage] = useState(initialStateRef.current.resultsPerPage);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialStateRef.current.sortDirection);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);

  const isInitialSystemChange = useRef(true);
  const isInitialResultsPerPage = useRef(true);
  const preserveUrlStateOnFirstSystemSync = useRef(hasAssetShareParams(window.location.search));

  useEffect(() => {
    if (isInitialSystemChange.current) {
      isInitialSystemChange.current = false;
      return;
    }

    if (preserveUrlStateOnFirstSystemSync.current) {
      preserveUrlStateOnFirstSystemSync.current = false;
      return;
    }

    setFilters(cloneDefaultAssetFilters());
    setPage(1);
  }, [selectedSystem]);

  useEffect(() => {
    if (isInitialResultsPerPage.current) {
      isInitialResultsPerPage.current = false;
      return;
    }
    setPage(1);
  }, [resultsPerPage]);

  const fetchAssets = useCallback(async () => {
    if (sessionIsLoading || !selectedSystem) {
      return;
    }

    setIsLoading(true);
    if (!selectedSystem) {
      setAssets([]);
      setTotalAssets(0);
      setIsLoading(false);
      return;
    }
    try {
      const {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
        ...otherFilters
      } = filters;

      const payload: Record<string, any> = { ...otherFilters };

      const imageFilters: Record<string, ImageFilter | undefined> = {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
      };

      for (const key in imageFilters) {
        const filter = imageFilters[key];
        if (filter?.checked) {
          payload[key] = {
            checked: true,
            operator: filter.operator || '=', // Default to '=' if not set
            imageCount: filter.imageCount ?? 0,   // Default to 0 if not set
          };
        }
      }

      const body = {
        ...payload,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem.systemCode,
        sort_direction: sortDirection,
      };
      const { assets: translatedAssets, total } = await getAssetsApi(body);
      setAssets(translatedAssets);
      setTotalAssets(total);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSystem, filters, page, resultsPerPage, sortDirection]);

  useEffect(() => {
    saveAssetsNavState({ page, filters, resultsPerPage, sortDirection });
  }, [page, filters, resultsPerPage, sortDirection]);

  useEffect(() => {
    const nextParams = buildAssetsSearchParams(
      searchParams,
      { page, filters, resultsPerPage, sortDirection },
      selectedSystem?.systemCode
    );

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, page, resultsPerPage, searchParams, selectedSystem?.systemCode, setSearchParams, sortDirection]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchAssets();
    }, 150);
    return () => clearTimeout(debounceTimer);
  }, [fetchAssets]);

  const updateFilters = (newFilters: AssetFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  return {
    assets,
    totalAssets,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
    sortDirection,
    setSortDirection,
    filters,
    updateFilters,
    refetch: fetchAssets,
    selectedAssets,
    setSelectedAssets
  };
};
